import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';
import { useSubscription } from '../../hooks/useSubscription';
import { useUserProfile } from '../../hooks/useUserProfile';
import EnterpriseContactForm from './EnterpriseContactForm';
import PaymentStatus from './PaymentStatus';
import { Modal } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

interface Plan {
  id: number;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  interval: string;
  daily_recording_limit: number | null;
  total_recording_limit: number | null;
  max_recording_duration: number | null;
  stripe_price_id: string | null;
  features: Record<string, any>;
}

interface ErrorModalProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  primaryAction?: {
    text: string;
    onClick: () => void;
  };
}

const ErrorModal: React.FC<ErrorModalProps> = ({ 
  visible, 
  onClose, 
  title, 
  message, 
  type,
  primaryAction
}) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined className="text-green-500 text-4xl" />;
      case 'warning':
      case 'error':
        return <ExclamationCircleOutlined className="text-red-500 text-4xl" />;
      case 'info':
      default:
        return <InfoCircleOutlined className="text-blue-500 text-4xl" />;
    }
  };
  
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={450}
      className="subscription-modal"
      closable={false}
      centered
    >
      <div className="text-center py-6 px-2">
        <div className="mb-4">
          {getIcon()}
        </div>
        <h3 className="text-xl font-bold mb-2">{title || "Choose Your Plan"}</h3>
        <p className="text-gray-600 mb-6">{message || "Select the plan that best fits your needs"}</p>
        <div className="flex justify-center space-x-3">
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              className="bg-[#0078d4] text-white px-4 py-2 rounded hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2"
            >
              {primaryAction.text}
            </button>
          )}
          <button
            onClick={onClose}
            className="border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

const PricingPlans: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState<number | null>(null);
  const [couponCode, setCouponCode] = useState<string>('');
  const [isCouponValid, setIsCouponValid] = useState<boolean | null>(null);
  const [couponMessage, setCouponMessage] = useState<string>('');
  const { isExpired, status, isTrial, isPaid, refreshSubscription } = useSubscription();
  const { profile, isDeveloper } = useUserProfile();
  const [showEnterpriseForm, setShowEnterpriseForm] = useState(false);
  
  // Error modal state
  const [errorModal, setErrorModal] = useState<{
    visible: boolean;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'success' | 'error';
    primaryAction?: {
      text: string;
      onClick: () => void;
    };
  }>({
    visible: false,
    title: '',
    message: '',
    type: 'info'
  });
  
  useEffect(() => {
    fetchPlans();
  }, []);
  
  const fetchPlans = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Determine user_type based on profile
      const userType = isDeveloper ? 'organization' : 'individual';

      const response = await axios.get('/api/scribing/subscriptions/plans', {
        params: {
          platform: 'web',
          user_type: userType
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setPlans(response.data);
    } catch (err: any) {
      console.error('Error fetching subscription plans:', err);
      setError('Failed to load subscription plans. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const closeErrorModal = () => {
    setErrorModal(prev => ({ ...prev, visible: false }));
  };
  
  const handleSubscribe = async (plan: Plan) => {
    try {
      if (!plan.stripe_price_id) {
        console.log('This is a free plan with no payment required');
        return;
      }
      
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      setProcessingPayment(plan.id);
      
      // Set the success and cancel URLs
      const successUrl = `${window.location.origin}/billing/success`;
      const cancelUrl = `${window.location.origin}/billing/cancel`;
      
      // Create request payload with optional coupon code
      const payload: any = {
        plan_id: plan.id,
        success_url: successUrl,
        cancel_url: cancelUrl
      };
      
      // Add coupon code if provided
      if (couponCode.trim()) {
        payload.coupon_code = couponCode.trim();
      }
      
      console.log('Initiating checkout for plan:', plan.name);
      const response = await axios.post('/api/scribing/subscriptions/checkout', 
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      console.log('Checkout response:', response.data);
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL provided');
      }
    } catch (err: any) {
      console.error('Error subscribing to plan:', err);
      setProcessingPayment(null);
      
      // Handle specific error cases
      if (err.response && err.response.status === 400) {
        const errorMessage = err.response.data?.detail || err.response.data?.message || 'An error occurred with your request.';
        
        // Parse specific error messages
        if (errorMessage.includes('already have an active subscription')) {
          // Handle duplicate subscription case
          refreshSubscription(); // Refresh to make sure our UI is up to date
          
          setErrorModal({
            visible: true,
            title: 'You already have this plan',
            message: 'You already have an active subscription to this plan. Would you like to view your current subscription details?',
            type: 'info',
            primaryAction: {
              text: 'View My Subscription',
              onClick: () => {
                closeErrorModal();
                window.location.href = '/settings'; // Redirect to settings
              }
            }
          });
          return;
        }
        
        // Handle other 400 errors
        setErrorModal({
          visible: true,
          title: 'Subscription Error',
          message: errorMessage,
          type: 'error'
        });
      } else {
        // Handle generic errors
        setErrorModal({
          visible: true,
          title: 'Something went wrong',
          message: 'We encountered an error while processing your subscription. Please try again later or contact support.',
          type: 'error'
        });
      }
    }
  };
  
  // Handle coupon code input changes
  const handleCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCouponCode(e.target.value);
    setIsCouponValid(null);
    setCouponMessage('');
  };
  
  // Validate coupon code
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setIsCouponValid(false);
      setCouponMessage('Please enter a coupon code');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // API endpoint for coupon validation would be implemented here
      // This is a placeholder for the actual validation logic
      setCouponMessage('Coupon applied successfully');
      setIsCouponValid(true);
      
    } catch (err: any) {
      console.error('Error validating coupon:', err);
      setIsCouponValid(false);
      setCouponMessage('Invalid coupon code');
    }
  };
  
  const formatCurrency = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };
  
  const formatInterval = (interval: string) => {
    switch(interval) {
      case 'month': return '/month';
      case 'year': return '/year';
      case 'week': return '/week';
      case 'day': return '/day';
      default: return `/${interval}`;
    }
  };

  // Sort plans by price (low to high)
  const sortedPlans = [...plans].sort((a, b) => a.price - b.price);
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Payment status notification */}
      <PaymentStatus />
      
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg 
            className="h-5 w-5 mr-2" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M10 19l-7-7m0 0l7-7m-7 7h18" 
            />
          </svg>
          Back
        </button>
      </div>
      
      <div className="flex-grow max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Choose Your Plan
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
            Select the plan that best fits your needs
          </p>
          
          {/* Coupon Code Input */}
          <div className="mt-6 max-w-md mx-auto flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
            <div className="flex-grow">
              <input 
                type="text"
                placeholder="Have a coupon code?"
                value={couponCode}
                onChange={handleCouponChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={validateCoupon}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Apply
            </button>
          </div>
          
          {/* Coupon status message */}
          {couponMessage && (
            <div className={`mt-2 text-sm ${isCouponValid ? 'text-green-600' : 'text-red-600'}`}>
              {couponMessage}
            </div>
          )}
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0078d4]"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md max-w-md mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={fetchPlans} 
              className="px-4 py-2 bg-[#0078d4] text-white rounded hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sortedPlans.map((plan) => {
              const isFree = plan.price === 0;
              const isPopular = !isFree && plan.name.toLowerCase().includes('starter');
              const isProcessing = processingPayment === plan.id;
              const isUnavailable = isFree && isExpired;
              
              return (
                <div 
                  key={plan.id} 
                  className={`bg-white rounded-lg overflow-hidden transition-shadow hover:shadow-md ${
                    isPopular 
                      ? 'border-2 border-[#0078d4] shadow-lg transform md:scale-105 md:-translate-y-2 z-10' 
                      : isUnavailable
                        ? 'border-gray-200 opacity-70'
                        : 'border border-gray-200'
                  }`}
                >
                  {isPopular && (
                    <div className="bg-[#0078d4] text-white text-sm font-medium py-1.5 text-center">
                      RECOMMENDED
                    </div>
                  )}
                  
                  <div className={`p-8 ${isPopular ? 'pt-6' : ''}`}>
                    <h3 className={`text-xl font-semibold text-gray-900 mb-3 ${isUnavailable ? 'line-through' : ''}`}>{plan.name}</h3>
                    {plan.description && <p className={`text-gray-600 mb-5 text-sm ${isUnavailable ? 'line-through' : ''}`}>{plan.description}</p>}
                    
                    <div className={`mb-5 ${isUnavailable ? 'line-through' : ''}`}>
                      {isFree ? (
                        <span className="text-4xl font-bold text-gray-900">Free</span>
                      ) : (
                        <div className="flex items-baseline gap-2">
                          {isCouponValid && (
                            <span className="text-lg text-gray-400 line-through">
                              {formatCurrency(plan.price, plan.currency)}
                            </span>
                          )}
                          <span className="text-4xl font-bold text-gray-900">
                            {isCouponValid
                              ? formatCurrency(plan.price * 0.8, plan.currency)
                              : formatCurrency(plan.price, plan.currency)
                            }
                          </span>
                          {plan.interval && (
                            <span className="text-gray-600">{formatInterval(plan.interval)}</span>
                          )}
                        </div>
                      )}
                      {isFree && (
                        <p className="text-sm text-gray-500 mt-1">No credit card required</p>
                      )}
                    </div>
                    
                    {isUnavailable ? (
                      <div className="w-full px-4 py-2 text-center bg-gray-100 text-gray-500 rounded-md font-medium">
                        No Longer Available
                      </div>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(plan)}
                        className={`w-full py-3 px-6 rounded-md font-medium mb-6 transition-colors ${
                          plan.stripe_price_id && !isFree
                            ? isProcessing
                              ? 'bg-gray-100 text-gray-400 cursor-wait'
                              : isPopular 
                                ? 'bg-[#0078d4] text-white hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2'
                                : 'bg-white text-[#0078d4] border border-[#0078d4] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2' 
                            : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        }`}
                        disabled={!plan.stripe_price_id || isFree || !!processingPayment}
                      >
                        {isFree ? 'Current Plan' : 
                         isProcessing ? (
                           <span className="flex items-center justify-center">
                             <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                             </svg>
                             Processing...
                           </span>
                         ) : 'Subscribe Now'}
                      </button>
                    )}
                    
                    <hr className="my-6 border-gray-200" />
                    
                    <h4 className="text-sm font-medium text-gray-900 mb-4">Plan includes:</h4>
                    <ul className={`space-y-4 mb-6 ${isUnavailable ? 'line-through text-gray-400' : ''}`}>
                      {/* Dynamic Features from plan.features */}
                      {plan.features && Object.entries(plan.features).map(([key, value]) => (
                        <li key={key} className="flex items-start">
                          <svg className={`h-5 w-5 ${isUnavailable ? 'text-gray-400' : 'text-[#107c10]'} mr-3 flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className={`${isUnavailable ? 'text-gray-400' : 'text-gray-700'}`}>
                            {value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
            
            {/* Enterprise Plan Card */}
            <div className="bg-white rounded-lg overflow-hidden transition-shadow hover:shadow-md border border-gray-200">
              <div className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Enterprise</h3>
                <p className="text-gray-600 mb-5 text-sm">Custom solutions for organizations with advanced needs and larger teams</p>
                
                <div className="mb-5">
                  <span className="text-4xl font-bold text-gray-900">Custom</span>
                  <span className="text-gray-600 ml-2">pricing</span>
                </div>
                
                <button
                  onClick={() => setShowEnterpriseForm(true)}
                  className="w-full py-3 px-6 rounded-md font-medium mb-6 transition-colors bg-[#0078d4] text-white hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2"
                >
                  Contact Sales
                </button>
                
                <hr className="my-6 border-gray-200" />
                
                <h4 className="text-sm font-medium text-gray-900 mb-4">Plan includes:</h4>
                <ul className="space-y-4 mb-6">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#107c10] mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700"><strong>Unlimited</strong> recordings</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#107c10] mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700"><strong>Unlimited</strong> recording duration</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#107c10] mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700"><strong>Dedicated</strong> account manager</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#107c10] mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700"><strong>Priority</strong> 24/7 support</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#107c10] mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700"><strong>Custom</strong> integrations</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#107c10] mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700"><strong>Advanced</strong> security features</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        <div className="mt-12 text-center text-sm text-gray-500 bg-white rounded-lg p-6 shadow-sm max-w-3xl mx-auto">
          <p className="mb-2">All plans include automatic updates and 99.9% uptime guarantee.</p>
          <p>Need a custom plan for your organization? <button className="text-[#0078d4] font-medium hover:underline focus:outline-none">Contact our sales team</button></p>
        </div>
      </div>
      
      {/* Error Modal */}
      <ErrorModal
        visible={errorModal.visible}
        onClose={closeErrorModal}
        title={errorModal.title}
        message={errorModal.message}
        type={errorModal.type}
        primaryAction={errorModal.primaryAction}
      />
      
      {/* Enterprise Contact Form */}
      <EnterpriseContactForm visible={showEnterpriseForm} onClose={() => setShowEnterpriseForm(false)} />
      
      <Footer />
    </div>
  );
};

export default PricingPlans; 